import type { SyntheticEvent } from 'react';
import Highlighter from 'react-highlight-words';
import styled from 'styled-components';
import type {
  AvatarColor,
  SuggestedAccountUser,
} from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import { Emoji } from '@frameio/shared/emoji';
import type { ThemeColorsBackground, ThemeColorsMeta } from '@frameio/vapor';
import {
  Avatar,
  Box,
  isMobile,
  Text,
  TextWithTruncation,
  theme,
  Tooltip,
} from '@frameio/vapor';
import { IconInfo, IconPending } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/MemberAccessRow';
import styles from './MemberAccessRow.module.css';
import {
  RowContainerButton,
  StyledIconPeople,
  StyledTextForMemberCount,
} from './MemberAccessRow.styles';

TEXT.DEFINE({
  /** Count of users */
  childCount: (count: number) =>
    plural(count, {
      one: '1 User',
      other: `${count > 100 ? '100+' : count} Users`,
    }),
  /** Label for guest name */
  guestName: (memberName: string) => `${memberName} · Guest`,
  /** Pending user label */
  pending: 'Pending User',
  /** Label for an already added user */
  alreadyHasAccess: 'Already has access',
  /** Tooltip text for duplicated access */
  tooltipHasAccess:
    'This user was already added or is an Admin or Content Owner',
});

export type MemberAccessRowProps = {
  avatarBackground?: ThemeColorsBackground | ThemeColorsMeta | AvatarColor;
  avatarUrl: string;
  emoji?: string;
  highlight?: boolean;
  isGroup?: boolean;
  isGuest?: boolean;
  isPending?: boolean;
  memberCount?: number;
  memberEmail?: string;
  memberId: SuggestedAccountUser['id'];
  memberName: string;
  onClick: (evt: SyntheticEvent<HTMLButtonElement>) => void;
  textInput?: string;
  truncateEmail?: boolean;
  hasAccess?: boolean;
};

export const MemberAccessRow = ({
  avatarBackground,
  avatarUrl,
  emoji,
  hasAccess,
  highlight,
  isGuest,
  isGroup,
  isPending,
  memberCount,
  memberEmail,
  memberId,
  memberName,
  textInput,
  onClick,
  truncateEmail,
}: MemberAccessRowProps) => {
  const searchWords = textInput?.split(' ') ?? [];
  const memberLabel = (
    isGuest ? TEXT.guestName((memberName || memberEmail) ?? '') : memberName
  ) as string;

  return (
    <RowContainerButton
      $isHighlighted={highlight}
      className={styles.row}
      disabled={hasAccess}
      name={memberId}
      onClick={onClick}
      // Prevent the default mousedown behavior which causes focus to shift away
      // from the input, which would unmount this dropdown before onClick can
      // fire. This allows the click event to complete normally while keeping
      // focus in place.
      onMouseDown={(e) => e.preventDefault()}
      type="button"
    >
      {isPending ? (
        <Avatar
          alt={memberName || memberEmail}
          aria-hidden="true"
          background={
            avatarBackground as ThemeColorsBackground | ThemeColorsMeta
          }
        >
          <IconPending color={theme.colors.contrast.icon.dark} size={16} />
        </Avatar>
      ) : (
        <Avatar
          alt={memberName || memberEmail}
          aria-hidden="true"
          background={
            emoji
              ? 'secondary'
              : (avatarBackground as ThemeColorsBackground | ThemeColorsMeta)
          }
          emojiComponent={emoji && <Emoji emoji={emoji} size={13} />}
          shape="circle"
          size={24}
          src={avatarUrl}
        />
      )}
      {/*  @ts-ignore */}
      <StyledTextWithTruncationForMemberName
        color="body"
        enableTooltip={true}
        role="text"
        tooltip={
          <Tooltip placement="top" title={memberName || memberEmail || ''} />
        }
        truncate={!isMobile}
        variant="body"
      >
        {/*
            @todo (VAPOR-493): Type errors will be resolved after vapor team refactors
            TextWithTruncation component, it only works with strings at the moment
            // @ts-ignore */}
        <>
          <Highlighter
            autoEscape={true}
            highlightStyle={{
              backgroundColor: theme.colors.background.interactive.highlight,
              color: theme.colors.text.body,
            }}
            searchWords={searchWords}
            textToHighlight={memberLabel || memberEmail || ''}
          />
        </>
      </StyledTextWithTruncationForMemberName>

      {isPending && (
        <Text className={styles.pendingText} color="tertiary" variant="body">
          {TEXT.pending}
        </Text>
      )}

      {memberEmail && !isPending && (
        <Text
          className={styles.memberEmail}
          color="tertiary"
          truncate={!isMobile && truncateEmail}
          variant="linkSmall"
        >
          {memberEmail}
        </Text>
      )}

      {hasAccess && (
        <Tooltip placement="top" title={TEXT.tooltipHasAccess}>
          <AlreadyHasAccessBadge>
            <Text color="tertiary" truncate={false} variant="linkSmall">
              {TEXT.alreadyHasAccess}
            </Text>
            <IconInfo size={16} />
          </AlreadyHasAccessBadge>
        </Tooltip>
      )}

      {isGroup && (
        <Box alignItems="center" display="inline-flex" flexShrink="0">
          <StyledIconPeople size={16} />
          <StyledTextForMemberCount variant="body">
            {TEXT.childCount(memberCount ?? 0)}
          </StyledTextForMemberCount>
        </Box>
      )}
    </RowContainerButton>
  );
};

const AlreadyHasAccessBadge = styled.div`
  align-items: center;
  color: ${theme.colors.text.tertiary};
  display: flex;
  gap: 6px;
  margin-left: auto;
  white-space: nowrap;
`;

export const StyledTextWithTruncationForMemberName = styled(TextWithTruncation)`
  margin-bottom: 0;
  margin-left: ${theme.spaces[8]};
  margin-right: ${theme.spaces[8]};
  margin-top: 0;
  max-width: 70%;
  text-align: left;
  white-space: nowrap;

  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 100%;
    white-space: normal;
  }
`;

export const StyledUl = styled.ul`
  list-style: none;
  text-align: left;
`;
